import React, { FC, useEffect, useState } from 'react';
import {
    Autocomplete,
    AutocompleteInputChangeReason,
    Box,
    ListItemIcon,
    ListItemText,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { User } from 'models/user';
import AddIcon from '@mui/icons-material/Add';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { getDentistsForLinked } from 'apollo/templates/user';
import { LinkedUser } from 'models/order';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { Organization } from 'models/organization';

type LinkedDentistsProps = {
    orderLinkedDentists: LinkedUser[];
    onAddDentists: (dentists: LinkedUser[]) => void;
};
const LinkedDentists: FC<LinkedDentistsProps> = ({ orderLinkedDentists, onAddDentists }: LinkedDentistsProps) => {
    const { t: translate } = useTranslation('orders');
    const [dentists, setDentists] = useState<User[]>();
    const [notLinkedDentists, setNotLinkedDentists] = useState<User[]>();
    const [linkedDentists, setLinkedDentists] = useState<LinkedUser[]>([]);
    const [inputValue, setInputValue] = useState('');
    const currentUser = currentUserVar();
    const { showBoundary } = useDenthubErrorBoundary();

    useEffect(() => {
        setLinkedDentists(orderLinkedDentists || []);
    }, [orderLinkedDentists]);

    const [getDentists, { loading }] = useLazyQuery(getDentistsForLinked, {
        onCompleted: ({ dentists }) => {
            setDentists(dentists);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (dentists && linkedDentists) {
            const notLinked = dentists.filter((dentist) => {
                return !linkedDentists.find((linkedDentist) => {
                    return linkedDentist.user._id === dentist._id;
                });
            });
            setNotLinkedDentists(notLinked);
        }
    }, [linkedDentists, dentists]);

    const addLinkedDentistUser = (dentist: User) => {
        if (linkedDentists) {
            const dentists = [
                ...linkedDentists,
                {
                    user: {
                        _id: dentist._id,
                        email: dentist.email,
                        fullName: dentist.fullName,
                    },
                },
            ] as LinkedUser[];
            setLinkedDentists(dentists);
            onAddDentists(dentists);
        }
    };

    const onDentistAutocompleteInputChange = (value: string, reason: AutocompleteInputChangeReason) => {
        if (reason === 'reset') {
            setInputValue('');
            setNotLinkedDentists([]);
            return;
        }
        setInputValue(value);
        getDentists({
            variables: {
                filters: { freeText: value, active: true },
                clinicId: (currentUser.organization as Organization)?._id,
            },
        });
    };

    return (
        <Box>
            <Box>
                <Autocomplete
                    fullWidth
                    disableClearable
                    options={notLinkedDentists || []}
                    getOptionLabel={(dentist) => dentist.fullName}
                    noOptionsText={translate('orders:TYPE_TO_SEARCH')}
                    renderOption={(props, dentist) => (
                        <MenuItem {...props} key={dentist._id} value={dentist._id}>
                            <ListItemIcon>
                                <AddIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                {dentist.fullName}
                                <Typography variant="body2" color="grey.700">
                                    {dentist.email}
                                </Typography>
                            </ListItemText>
                        </MenuItem>
                    )}
                    renderInput={(params) => <TextField {...params} label={translate('orders:ADD_PARTICIPANT_USER')} />}
                    inputValue={inputValue}
                    onChange={(_, dentist) => {
                        addLinkedDentistUser(dentist);
                    }}
                    onInputChange={(_, value, reason) => {
                        onDentistAutocompleteInputChange(value, reason);
                    }}
                />
            </Box>
        </Box>
    );
};

export default LinkedDentists;
